import { useState } from "react";
import { useMarketplaceConfig } from "./useMarketplace";
import { useSellableLand } from "./useSellLand";
import { useSession } from "./useSession";
import { SellLandWizard, type SellSourceParcel } from "./SellLandWizard";
import { EmptyState, ErrorState, MktButton, Tag } from "./primitives";
import { goMarketplace } from "./mkt-routing";
import { useT } from "../../i18n";
import type { SellableParcel } from "../../lib/marketplace-api";
import { cn } from "../cn";

const WORLD = 1000;
const ZONE_FILL = [
  "fill-neon-teal/30 stroke-neon-teal",
  "fill-neon-coral/25 stroke-neon-coral",
  "fill-zone-premium/25 stroke-zone-premium",
  "fill-zone-vip/25 stroke-zone-vip",
];

/**
 * Entry B (hybrid) — PREVIEW DEMO GRID surface. An ISOLATED 1000×1000 minimap
 * of the session wallet's demo land only. NOT the canonical GridViewer and it
 * never mutates canonical parcels / chain state. Clicking a parcel opens the
 * SAME shared seller wizard.
 */
export function PreviewDemoGrid() {
  const t = useT();
  const { data: config } = useMarketplaceConfig();
  const session = useSession();
  const enabled = !!config?.preview_demo && session.isAuthenticated;
  const { data, isLoading, isError, refetch } = useSellableLand(enabled);
  const [picked, setPicked] = useState<SellSourceParcel | null>(null);

  const parcels = data?.parcels ?? [];

  const open = (p: SellableParcel) =>
    setPicked({ parcel_id: p.parcel_id, x: p.x, y: p.y, w: p.w, h: p.h, owner: p.owner });

  return (
    <div data-testid="preview-demo-grid" className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Tag tone="coral" testId="demo-grid-badge">
              {t("marketplace.sell.previewDemo")}
            </Tag>
            <h2 className="font-display text-lg md:text-xl font-semibold tracking-[0.02em] text-text-primary uppercase">
              {t("marketplace.demoGrid.title")}
            </h2>
          </div>
          <p className="mt-1 font-mono text-[10.5px] tracking-[0.12em] uppercase text-text-muted">
            {t("marketplace.demoGrid.subtitle")}
          </p>
        </div>
        <MktButton variant="ghost" testId="demo-grid-return" onClick={() => goMarketplace("mine")}>
          {t("marketplace.demoGrid.returnToMarket")}
        </MktButton>
      </div>

      <div className="rounded-lg border border-border-coral bg-neon-coral/5 px-4 py-3">
        <p className="font-mono text-[10.5px] leading-relaxed text-text-muted">{t("marketplace.demoGrid.disclosure")}</p>
      </div>

      {!enabled ? (
        <EmptyState testId="demo-grid-signin" title={t("marketplace.demoGrid.empty")} hint={t("marketplace.demoGrid.emptyHint")} />
      ) : isLoading ? (
        <div className="py-16 text-center font-mono text-[11px] tracking-[0.2em] uppercase text-text-muted animate-pulse">
          {t("marketplace.states.loading")}
        </div>
      ) : isError ? (
        <ErrorState title={t("marketplace.demoGrid.loadError")} onRetry={() => refetch()} testId="demo-grid-error" />
      ) : parcels.length === 0 ? (
        <EmptyState testId="demo-grid-empty" title={t("marketplace.demoGrid.empty")} hint={t("marketplace.demoGrid.emptyHint")} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
          {/* Minimap */}
          <div className="relative rounded-xl border border-border-divider bg-base/60 p-3">
            <span className="absolute top-3 left-3 font-mono text-[9px] tracking-[0.2em] uppercase text-text-faint">
              {t("marketplace.demoGrid.worldLabel")}
            </span>
            <svg viewBox={`0 0 ${WORLD} ${WORLD}`} className="w-full aspect-square" data-testid="demo-grid-map">
              <rect x={0} y={0} width={WORLD} height={WORLD} className="fill-elevated/30 stroke-border-divider" strokeWidth={2} />
              {parcels.map((p, i) => (
                <g key={p.parcel_id} data-testid={`demo-map-parcel-${p.parcel_id}`}>
                  <rect
                    x={p.x}
                    y={p.y}
                    width={p.w}
                    height={p.h}
                    className={cn(ZONE_FILL[i % ZONE_FILL.length], "cursor-pointer transition-opacity hover:opacity-100 opacity-90")}
                    strokeWidth={2}
                    onClick={() => open(p)}
                  />
                  {p.active_listings > 0 && (
                    <circle cx={p.x + p.w} cy={p.y} r={8} className="fill-neon-coral" />
                  )}
                </g>
              ))}
            </svg>
          </div>

          {/* Parcel list */}
          <div className="space-y-2">
            <p className="font-mono text-[10px] tracking-[0.16em] uppercase text-text-muted">{t("marketplace.demoGrid.clickToSell")}</p>
            {parcels.map((p) => (
              <button
                key={p.parcel_id}
                type="button"
                data-testid={`demo-grid-parcel-${p.parcel_id}`}
                onClick={() => open(p)}
                className="w-full flex items-center justify-between gap-3 rounded-lg border border-border-divider bg-elevated/40 px-4 py-3 hover:border-neon-teal/50 hover:bg-elevated transition-[border-color,background-color] text-left"
              >
                <div className="flex flex-col">
                  <span className="font-display text-sm text-text-primary">Parcel #{p.parcel_id}</span>
                  <span className="font-mono text-[10px] text-text-muted tabular-nums">
                    {p.x},{p.y} · {p.w}×{p.h}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Tag tone="teal">{t("marketplace.demoGrid.ownedBadge")}</Tag>
                  {p.active_listings > 0 && <Tag tone="coral">{t("marketplace.demoGrid.listedBadge", { n: p.active_listings })}</Tag>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <SellLandWizard key={picked?.parcel_id ?? "none"} open={!!picked} sourceParcel={picked} onClose={() => setPicked(null)} />
    </div>
  );
}
